import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { Hand, MapPin } from 'lucide-react';
import { Button, Card, Chip } from '@heroui/react';

export type ParkingSpotChipColor = 'default' | 'accent' | 'success' | 'warning' | 'danger';

export interface ParkingSpotCardPrimaryAction {
  label: string;
  onPress: () => void;
  isDisabled?: boolean;
  variant?: 'primary' | 'secondary' | 'danger';
  icon?: LucideIcon;
}

interface ParkingSpotCardProps {
  code: string;
  description: string;
  chipLabel: string;
  chipColor: ParkingSpotChipColor;
  onViewLocation?: () => void;
  primaryAction?: ParkingSpotCardPrimaryAction;
  children?: ReactNode;
  /** Marca o card como alvo do tutorial guiado (só o primeiro da lista usa). */
  isTourTarget?: boolean;
}

export function ParkingSpotCard({
  code,
  description,
  chipLabel,
  chipColor,
  onViewLocation,
  primaryAction,
  children,
  isTourTarget,
}: ParkingSpotCardProps) {
  const hasFooter = Boolean(onViewLocation || primaryAction);

  return (
    <Card data-tour={isTourTarget ? 'spot-card' : undefined}>
      <Card.Header
        className={`flex-row items-start justify-between gap-3 ${children || hasFooter ? 'pb-0' : ''}`}
      >
        <div className="min-w-0">
          <Card.Title className="font-mono text-lg font-bold tracking-tight">{code}</Card.Title>
          <Card.Description>{description}</Card.Description>
        </div>
        <Chip size="sm" variant="soft" color={chipColor}>
          {chipLabel}
        </Chip>
      </Card.Header>
      {children ? <Card.Content className="pt-3">{children}</Card.Content> : null}
      {hasFooter ? (
        <Card.Footer className="grid grid-cols-2 gap-2 pt-0">
          {onViewLocation ? (
            <Button variant="outline" className="w-full" onPress={onViewLocation}>
              <MapPin size={16} />
              Ver no mapa
            </Button>
          ) : null}
          {primaryAction ? (
            <Button
              variant={primaryAction.variant ?? 'primary'}
              className={`w-full ${onViewLocation ? '' : 'col-span-2'}`.trim()}
              isDisabled={primaryAction.isDisabled}
              onPress={primaryAction.onPress}
              data-tour={isTourTarget ? 'spot-request-button' : undefined}
            >
              {(() => {
                const Icon = primaryAction.icon ?? Hand;
                return <Icon size={16} />;
              })()}
              {primaryAction.label}
            </Button>
          ) : null}
        </Card.Footer>
      ) : null}
    </Card>
  );
}
